// TeamDetailLive.js
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useTheme, useMediaQuery } from "@mui/material";
import moment from "moment";
import Header from "./components/Header/Header";
import { useParams } from "react-router";
import { useGender } from "contexts/GenderContext";
import RankPointsChart from "./components/RankPointsChart/RankPointsChart";
import RankPositionChart from "./components/RankPositionChart/RankPositionChart";
import fetchWorldRanking from "hooks/getWorldRanking";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import teamCodeHashmap from "utils/teamCodeHashmap";
import MatchesPlayTable from "./components/MatchesPlayTable/MatchesPlayTable";
import MatchesPlayTableMobile from "./components/MatchesPlayTable/MatchesPlayTableMobile";
import MatchProbTable from "./components/MatchProbabilitiesTable/MatchProbabilitiesTable";
import MatchProbTableMobile from "./components/MatchProbabilitiesTable/MatchProbabilitiesTableMobile";
import appConfig from "appConfig";
import "assets/styles.css";

const TeamDetailLive = () => {
  const { teamName } = useParams();
  const { selectedGender } = useGender();
  const { instance, accounts } = useMsal(); // MSAL instance for token handling
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [rank, setRank] = useState("N/A");
  const [points, setPoints] = useState("N/A");
  const [confederationName, setConfederationName] = useState("N/A");
  const [confederationCode, setConfederationCode] = useState("N/A");
  const [error, setError] = useState(null);

  const genderValue = selectedGender === "Men" ? 1 : 0;

  const [apiDate] = useState(moment().format("YYYY-MM-DD"));

  const fetchTeamRankAndPoints = async () => {
    try {
      setError(null);

      if (!accounts || accounts.length === 0) {
        throw new Error("No account found");
      }

      const request = {
        scopes: appConfig.authScopes,
        account: accounts[0],
      };

      // Acquire token silently
      const accessTokenResponse = await instance.acquireTokenSilent(request);
      const accessToken = accessTokenResponse.accessToken;

      const worldRankingData = await fetchWorldRanking(
        apiDate,
        genderValue,
        accessToken
      );

      const teamEntry = teamCodeHashmap[teamName.toLowerCase()];

      if (!teamEntry) {
        console.warn(`Team code not found for team: ${teamName}`);
        return;
      }

      const teamCode = Object.keys(teamEntry)[0];

      const teamData = worldRankingData.find(
        (item) =>
          item.team_CalculatedCode_target.toLowerCase() ===
          teamCode.toLowerCase()
      );

      if (teamData) {
        setRank(teamData.n_WRanking);
        setPoints(parseFloat(teamData.f_WRS).toFixed(2));
        setConfederationName(teamData.confederation_name_target);
        setConfederationCode(teamData.confederation_code_target);
      } else {
        console.warn(`Team data not found for team code: ${teamCode}`);
      }
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect({
          scopes: appConfig.authScopes,
          account: accounts[0],
        });
      } else {
        setError(error.message);
      }
    }
  };

  useEffect(() => {
    fetchTeamRankAndPoints();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamName, selectedGender]);

  return (
    <Box>
      <Box>
        <Header
          teamName={teamName}
          rank={rank}
          points={points}
          confederationName={confederationName}
          confederationCode={confederationCode}
        />
      </Box>
      {error && <div>Error: {error}</div>}
      <Box className="BoxTD">
        {/* Conditionally render based on screen size */}
        <Box>
          {isMobile ? (
            <MatchesPlayTableMobile teamName={teamName} gender={genderValue} />
          ) : (
            <MatchesPlayTable teamName={teamName} gender={genderValue} />
          )}
        </Box>
        <Box className="chartsWfiltersBox">
          <Box className="chartsBox">
            <Box className="SingleChart">
              <div className="ChartTitleTypo">RANKING POINTS</div>
              <RankPointsChart
                teamName={teamName}
                apiDate={apiDate}
                gender={genderValue}
              />
            </Box>
            <Box className="SingleChart">
              <div className="ChartTitleTypo">RANKING POSITION</div>
              <RankPositionChart
                teamName={teamName}
                apiDate={apiDate}
                gender={genderValue}
              />
            </Box>
          </Box>
        </Box>
        <Box>
          {isMobile ? (
            <MatchProbTableMobile teamName={teamName} gender={genderValue} />
          ) : (
            <MatchProbTable teamName={teamName} gender={genderValue} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TeamDetailLive;
